import { IconComponent } from '../icon/icon.component';
import { PrintfileReportMessageComponent } from '../printfile-report-message/printfile-report-message.component';
import { AsyncPipe, DecimalPipe, NgClass } from '@angular/common';
import {
	Component,
	OnInit,
	booleanAttribute,
	computed,
	inject,
	input,
	output,
	signal,
} from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormsModule } from '@angular/forms';
import { RootReducer, Store } from '@app/app.reducers';
import { PrintfileStatusButtonsComponent } from '@app/checkout/modules/checkout-shared/components/printfile-status-buttons/printfile-status-buttons.component';
import {
	CartItem,
	getPremediaPrices,
	modifyProductOptions,
} from '@app/checkout/modules/checkout-shared/reducers/checkout.reducer';
import {
	getCartItemFile,
	isPrintfileRequired,
} from '@app/checkout/modules/checkout-shared/utils/checkout-utils';
import { PreflightStatus } from '@app/file-api/file-api.constants';
import type { PrintfileReport, PrintfileReportDetail } from '@app/file-api/file-api.service';
import { getPreMediaPrice$ } from '@app/shared/utils/price-utils';
import { faSquareMinus } from '@fortawesome/pro-regular-svg-icons/faSquareMinus';
import { faSquarePlus } from '@fortawesome/pro-regular-svg-icons/faSquarePlus';

const NON_FIXABLE_ERRORS = new Set([
	'ERROR_RES',
	'ERROR_DMG',
	'ERROR_NO_PRINT',
	'ERROR_NO_HI_PRINT',
	'ERROR_FONT',
	'ERROR_CRITICAL_FAILURE',
	'ERROR_MISSING_PRODUCT',
]);

const NON_FIXABLE_WARNINGS = new Set(['WARNING_RES', 'WARNING_1B_RES']);

export type PrintfileReportDetailDto = api.PrintfileReportDetailDto & PrintfileReportDetail;
export type PrintfileReportDto = api.PrintfileReportDto & PrintfileReport;

/** Component to display printfile report. */
@Component({
	selector: 'g-printfile-report',
	templateUrl: './printfile-report.component.html',
	styleUrls: ['./printfile-report.component.scss'],
	imports: [
		FlexLayoutModule,
		FormsModule,
		AsyncPipe,
		PrintfileReportMessageComponent,
		IconComponent,
		NgClass,
		PrintfileStatusButtonsComponent,
	],
	providers: [DecimalPipe],
})
export class PrintfileReportComponent implements OnInit {
	private readonly store = inject(Store<RootReducer.State>);
	decimalPipe = inject(DecimalPipe);

	readonly item$ = input.required<CartItem>({ alias: 'item' });
	readonly readOnly$ = input(false, { alias: 'readOnly', transform: booleanAttribute });
	readonly changeClicked = output<Event>();

	showMessages = true;
	readonly premediaPrices$ = this.store.select(getPremediaPrices);
	readonly getPreMediaPrice$ = getPreMediaPrice$(this.premediaPrices$, this.decimalPipe, 'printfileFix');
	readonly icons = { faSquarePlus, faSquareMinus };

	readonly fixMessage = signal<string>('');

	/** Initializes the component and sets the initial fix message. */
	ngOnInit(): void {
		this.fixMessage.set(this.item$().options?.selectedOptions?.premedia_fix_request_message ?? '');
	}

	readonly isFixRequested$ = computed(() => {
		return !!this.item$().options.selectedOptions?.is_premedia_fix_requested;
	});

	readonly report$ = computed<PrintfileReportDto | undefined>(() => {
		return this.item$().options?.printfile?.report as PrintfileReportDto;
	});

	readonly isPrintable$ = computed<boolean>(() => {
		const report = this.report$();
		return (report?.is_printable || report?.status === PreflightStatus.PRINTABLE) ?? false;
	});

	readonly errors$ = computed<PrintfileReportDetailDto[]>(() => {
		return (this.report$()?.errors ?? []) as PrintfileReportDetailDto[];
	});

	readonly warnings$ = computed<PrintfileReportDetailDto[]>(() => {
		return (this.report$()?.warnings ?? []) as PrintfileReportDetailDto[];
	});

	readonly hasMessages$ = computed<boolean>(() => {
		return this.errors$().length > 0 || this.warnings$().length > 0;
	});

	readonly hasPrintfile$ = computed<boolean>(() => {
		return !!this.item$() && isPrintfileRequired(this.item$()) && !!getCartItemFile(this.item$());
	});

	readonly isFixable$ = computed(() => {
		const areAllErrorsFixable =
			!this.isPrintable$() &&
			this.errors$().every(
				(detail) => !NON_FIXABLE_ERRORS.has(detail.errorCode || detail.error_code || ''),
			);

		// TODO: Warn the user if there are fixable errors with non-fixable warnings?
		if (areAllErrorsFixable) return true;

		const warnings = this.warnings$();
		const areAllWarningsFixable =
			this.isPrintable$() &&
			warnings.length > 0 &&
			warnings.every((detail) => !NON_FIXABLE_WARNINGS.has(detail.errorCode || detail.error_code || ''));

		return areAllWarningsFixable;
	});

	/**
	 * Determines the CSS class for the report based on its state and whether a fix is requested.
	 */
	readonly colorClass$ = computed((): Record<string, boolean> => {
		const isPrintable = this.isPrintable$();
		const isFixRequested = this.isFixRequested$();
		return {
			error:
				((!isPrintable && this.errors$().length > 0) || !this.item$().options?.printfile) &&
				!isFixRequested,
			warning: (isPrintable && this.warnings$().length > 0) || isFixRequested,
		};
	});

	/**
	 * Toggles the premedia fix request status and updates the selected options.
	 * @param fixRequested - The new status of the fix request.
	 */
	toggleFixRequest(fixRequested: boolean): void {
		if (!fixRequested) {
			this.fixMessage.set(''); // Clear local message signal when unchecking
		}
		this.updateSelectedOptions({
			is_premedia_fix_requested: fixRequested,
			// Preserve message if checking, clear if unchecking
			premedia_fix_request_message: fixRequested ? this.fixMessage() : '',
		});
	}

	/** Updates the premedia fix request message in the selected options. */
	updateFixRequestMessage(): void {
		this.updateSelectedOptions({ premedia_fix_request_message: this.fixMessage() });
	}

	/**
	 * Updates the selected product options in the store.
	 * @param options - The partial selected options to update.
	 */
	private updateSelectedOptions(options: Partial<api.OrderProductSelectedOptionsDto>) {
		const item = this.item$();
		// istanbul ignore if -- early exit
		if (!item) return;
		this.store.dispatch(
			modifyProductOptions(item.id, {
				...item.options,
				selectedOptions: { ...item.options.selectedOptions, ...options },
			}),
		);
	}
}
